import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle2, XCircle, Loader2, ShoppingBag } from 'lucide-react';
import { getCheckoutStatus, fetchOrderBySession, clearCart } from '../api';

const MAX_POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('checking'); // checking | success | failed | timeout
  const [order, setOrder] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      setErrorMsg('No session ID provided');
      return;
    }

    pollPaymentStatus(sessionId, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    if (attempts >= MAX_POLL_ATTEMPTS) {
      setStatus('timeout');
      return;
    }

    try {
      const statusData = await getCheckoutStatus(sid);

      if (statusData.payment_status === 'paid') {
        // Payment successful - load order and clear cart
        try {
          const orderData = await fetchOrderBySession(sid);
          setOrder(orderData);
        } catch (e) {
          console.warn('Could not load order details', e);
        }
        clearCart();
        window.dispatchEvent(new Event('cartUpdated'));
        setStatus('success');
        return;
      }

      if (statusData.status === 'expired') {
        setStatus('failed');
        setErrorMsg('Payment session expired');
        return;
      }

      // Still pending - poll again
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), POLL_INTERVAL_MS);
    } catch (err) {
      console.error('Status check error:', err);
      if (attempts < MAX_POLL_ATTEMPTS - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), POLL_INTERVAL_MS);
      } else {
        setStatus('failed');
        setErrorMsg('Failed to verify payment status');
      }
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-[8%] flex items-center justify-center">
      <Card className="bg-[#111] border-[#222] p-10 max-w-xl w-full text-center" data-testid="checkout-result">
        {status === 'checking' && (
          <div data-testid="status-checking">
            <Loader2 className="w-20 h-20 mx-auto mb-6 text-[#ff1f4d] animate-spin" />
            <h1 className="text-3xl font-bold text-white mb-3">Processing Payment...</h1>
            <p className="text-gray-400">Please wait while we confirm your order</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="status-success">
            <CheckCircle2 className="w-20 h-20 mx-auto mb-6 text-green-500" />
            <h1 className="text-4xl font-black text-[#ff1f4d] mb-3">Order Confirmed!</h1>
            <p className="text-gray-300 mb-6">
              Thank you for shopping with 2.TURNT INDUSTRIES
            </p>

            {order && (
              <div className="bg-black border border-[#222] rounded-lg p-4 text-left mb-6">
                <p className="text-gray-400 text-sm mb-2">Order ID</p>
                <p className="text-white font-mono mb-4">{order.id}</p>

                <p className="text-gray-400 text-sm mb-2">Items</p>
                <ul className="text-white text-sm space-y-1 mb-4">
                  {order.items?.map((it, idx) => (
                    <li key={idx}>
                      {it.quantity}× {it.name} ({it.size}) — ${(it.price * it.quantity).toFixed(2)}
                    </li>
                  ))}
                </ul>

                <div className="border-t border-[#222] pt-3 flex justify-between">
                  <span className="text-gray-400">Total Paid</span>
                  <span className="text-[#ff1f4d] font-bold text-lg">
                    ${order.total?.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <Button
              onClick={() => navigate('/')}
              className="w-full bg-[#ff1f4d] hover:bg-white hover:text-black font-bold py-6"
              data-testid="return-home-btn"
            >
              <ShoppingBag className="mr-2" /> Continue Shopping
            </Button>
          </div>
        )}

        {status === 'failed' && (
          <div data-testid="status-failed">
            <XCircle className="w-20 h-20 mx-auto mb-6 text-red-500" />
            <h1 className="text-3xl font-bold text-white mb-3">Payment Issue</h1>
            <p className="text-gray-400 mb-6">{errorMsg || 'Something went wrong with your payment'}</p>
            <Button
              onClick={() => navigate('/cart')}
              className="bg-[#ff1f4d] hover:bg-white hover:text-black font-bold"
              data-testid="back-to-cart-btn"
            >
              Back to Cart
            </Button>
          </div>
        )}

        {status === 'timeout' && (
          <div data-testid="status-timeout">
            <Loader2 className="w-20 h-20 mx-auto mb-6 text-yellow-500" />
            <h1 className="text-3xl font-bold text-white mb-3">Verification Timeout</h1>
            <p className="text-gray-400 mb-6">
              We couldn't confirm your payment status in time. If you were charged, you'll receive
              a confirmation email shortly.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-[#ff1f4d] hover:bg-white hover:text-black font-bold"
            >
              Return Home
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};
